import { faFloppyDisk, faPen, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  InputLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { useAtom } from 'jotai';
import { useMemo, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

import CheckPermissions from '@/components/CheckPermissions';
import LargeCard from '@/components/LargeCard';
import { PERMISSIONS } from '@/constants/auth.constants';
import { selectedOrganizationAtom } from '@/pages/Organizations';

import {
  EMAIL_SUBJECT_DEFAULT_TEMPLATE,
  EMAIL_SUBJECT_DEFAULT_TEMPLATE_PLACEHOLDER,
  EMAIL_SUBJECT_MAX_LENGTH,
  EMAIL_SUBJECT_TICKET_PROPERTIES
} from './EmailSubjectTemplate.constants';
import { useSendTestEmail } from './EmailSubjectTemplate.hooks';
import type {
  EmailSubjectTemplateFormValues,
  EmailSubjectTemplateProps,
  TestEmailFormValues
} from './EmailSubjectTemplate.types';
import EmailSubjectTestEmailDialog from './EmailSubjectTestEmailDialog';

const EmailSubjectTemplate = (props: EmailSubjectTemplateProps) => {
  const theme = useTheme();
  const { orgInfo, handleSave } = props;
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const textFieldRef = useRef<HTMLInputElement>(null);
  const sendTestEmail = useSendTestEmail();

  const defaultFormValues: EmailSubjectTemplateFormValues = {
    enabled: !!orgInfo.email_subject_template,
    template: orgInfo.email_subject_template || ''
  };

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isDirty }
  } = useForm<EmailSubjectTemplateFormValues>({
    defaultValues: defaultFormValues,
    values: defaultFormValues,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit'
  });

  const enabled = useWatch({ control, name: 'enabled' });
  const template = useWatch({ control, name: 'template' });

  // Generate preview when template changes
  const previewText = useMemo(() => {
    if (!enabled || !template) return '';
    let preview = template;
    EMAIL_SUBJECT_TICKET_PROPERTIES.forEach((prop) => {
      const regex = new RegExp(`\\{\\{${prop.value}\\}\\}`, 'g');
      preview = preview.replace(regex, prop.example);
    });
    return preview;
  }, [enabled, template]);

  const handleToggleChange = (checked: boolean) => {
    setValue('enabled', checked, { shouldDirty: true });
    if (!checked) {
      setValue('template', '', { shouldDirty: true });
    } else {
      setValue('template', EMAIL_SUBJECT_DEFAULT_TEMPLATE, {
        shouldDirty: true
      });
    }
  };

  const handlePropertyInsert = (propertyValue: string) => {
    const input = textFieldRef.current;
    if (!input) return;

    const cursorPosition = input.selectionStart || 0;
    const currentTemplate = template || '';
    const propertyToken = `{{${propertyValue}}}`;

    const newTemplate =
      currentTemplate.slice(0, cursorPosition) +
      propertyToken +
      currentTemplate.slice(cursorPosition);

    setValue('template', newTemplate, { shouldDirty: true });

    // Set cursor position after the inserted property
    setTimeout(() => {
      const newPosition = cursorPosition + propertyToken.length;
      input.focus();
      input.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const onSubmit = async (data: EmailSubjectTemplateFormValues) => {
    if (isSaving) return;

    setIsSaving(true);
    if (!isDirty) {
      setIsEditing(false);
      setIsSaving(false);
      return;
    }

    try {
      await handleSave({
        ...orgInfo,
        email_subject_template: data.enabled ? data.template : null
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestEmail = () => {
    setTestDialogOpen(true);
  };

  const handleSendTestEmail = ({ email, action }: TestEmailFormValues) => {
    sendTestEmail.mutate({
      organization_label: selectedOrganization.label,
      email,
      action
    });
  };

  return (
    <LargeCard
      title="Ticket Email Notification Subject"
      rightContent={
        <CheckPermissions permissions={PERMISSIONS.ADMIN_UPDATE}>
          {isEditing ? (
            <Stack direction="row">
              <IconButton onClick={handleSubmit(onSubmit)} disabled={isSaving}>
                <FontAwesomeIcon size="xs" icon={faFloppyDisk} />
              </IconButton>
              <IconButton
                onClick={() => {
                  setIsEditing(false);
                  reset(defaultFormValues);
                }}
              >
                <FontAwesomeIcon size="xs" icon={faXmark} />
              </IconButton>
            </Stack>
          ) : (
            <IconButton onClick={() => setIsEditing(true)}>
              <FontAwesomeIcon size="xs" icon={faPen} />
            </IconButton>
          )}
        </CheckPermissions>
      }
    >
      <Stack spacing={2}>
        {/* Toggle Section */}
        <Controller
          name="enabled"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  checked={field.value}
                  onChange={(e) => {
                    field.onChange(e);
                    handleToggleChange(e.target.checked);
                  }}
                  disabled={!isEditing}
                />
              }
              label="Enable Custom Email Subject"
            />
          )}
        />

        {/* Template Editor - Shown when enabled */}
        {enabled && (
          <>
            <InputLabel>Subject Template</InputLabel>
            <Controller
              name="template"
              control={control}
              rules={{
                maxLength: {
                  value: EMAIL_SUBJECT_MAX_LENGTH,
                  message: `Subject template must be less than ${EMAIL_SUBJECT_MAX_LENGTH} characters`
                }
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  inputRef={textFieldRef}
                  fullWidth
                  disabled={!isEditing}
                  placeholder={EMAIL_SUBJECT_DEFAULT_TEMPLATE_PLACEHOLDER}
                  helperText={
                    fieldState.error?.message ||
                    'Use {{property}} format to insert dynamic values'
                  }
                  error={!!fieldState.error}
                  style={{ marginTop: '.5rem' }}
                  slotProps={{
                    inputLabel: { style: { fontFamily: 'monospace' } }
                  }}
                  sx={{
                    '.MuiInputBase-input': {
                      fontFamily: 'monospace'
                    }
                  }}
                />
              )}
            />

            {/* Property Insertion Tool */}
            <Box>
              <InputLabel>Available Ticket Property</InputLabel>
              <Autocomplete
                options={EMAIL_SUBJECT_TICKET_PROPERTIES}
                getOptionLabel={(option) => option.label}
                renderOption={(liProps, option) => (
                  <li {...liProps}>
                    <Box>
                      <Typography sx={{ fontWeight: 800 }} variant="body1">
                        {option.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.description}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontStyle: 'italic' }}
                      >
                        {`{{${option.value}}}`} → {option.example}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Property to Insert"
                    size="small"
                    style={{ marginTop: '0.5rem' }}
                  />
                )}
                disabled={!isEditing}
                sx={{ width: '30rem' }}
                onChange={(_, value) => {
                  if (value) {
                    handlePropertyInsert(value.value);
                  }
                }}
                value={null}
              />
            </Box>

            {previewText && (
              <Paper
                elevation={0}
                sx={{
                  border: `solid 1px ${theme.vars.palette.divider}`,
                  borderRadius: 1,
                  p: 2
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Preview with example data:
                </Typography>
                <Typography variant="h5" sx={{ mt: 0.5 }}>
                  {previewText}
                </Typography>
              </Paper>
            )}

            {/* Test Email Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleTestEmail}
                disabled={isEditing || (template ?? '').trim() === ''}
              >
                Send Test Email
              </Button>
            </Box>
          </>
        )}
      </Stack>

      {testDialogOpen && (
        <EmailSubjectTestEmailDialog
          open={testDialogOpen}
          onClose={() => setTestDialogOpen(false)}
          onSend={handleSendTestEmail}
          isSaving={sendTestEmail.isPending}
        />
      )}
    </LargeCard>
  );
};

export default EmailSubjectTemplate;
